import { BootstrapResponse } from '@ovotech/energy-cx';
import {
  AnalyticsData,
  useAccountContext,
  useCustomerContext,
} from '@ovotech/ui-tools';
import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom-v5';
import { Main } from './Main';
import SiteLoading from './SiteLoading';
import { useDirectDebitQuery } from '@/src/api';

import { GlobalErrorPage } from '@/src/components';
import { BOOTSTRAP_ERROR } from '@/src/constants/error-section-ids';
import {
  ROUTE_HELP,
  ROUTE_HOME,
  ROUTE_PAYX,
  ROUTE_SELECT_ACCOUNT,
} from '@/src/constants/routes';
import { optimizely } from '@/src/utils/optimizely';
import { useQualtricsDataEmbed } from '@/src/hooks/Qualtrics/useQualtricsDataEmbed';
import { getBootstrapSuccess } from '@/src/redux/ducks/bootstrap/bootstrap';
import { getRecommendedDirectDebitStart } from '@/src/redux/ducks/recommendedDirectDebit/recommendedDirectDebit';
import { RecommendedDirectDebitResponse } from '@/src/types/Response';
import { SelectedAccountSupply, State } from '@/src/types/State';
import devAuth from '@/src/utils/devAuth';
import spaLoaded from '@/src/utils/spa-loaded';
import { setInitialUserContext as setInitialUserErrorTrackingContext } from '@/src/services/datadog';

import { useCustomerIdQuery } from '@/src/api/utils/useCustomerIdQuery';
import { useBootstrapQuery } from '@/src/api/kapi/use-bootstrap-query';
import { StyledLoadingBackground } from '@/src/components/layout';
import { NewWebRedirectGate } from '@/src/components/NewWebRedirect/NewWebRedirectGate';
import { useTariffType } from '@/src/hooks/useTariffType';
import {
  AccountBillingType,
  useAccountBillingType,
} from '@/src/hooks/useAccountBillingType';

type MeterType = 'smart' | 'trad' | null;
const getMeterType = (supply: SelectedAccountSupply): MeterType => {
  if (!supply || !supply.supplyPointInfo) {
    return null;
  }
  return supply.supplyPointInfo.isSmart ? 'smart' : 'trad';
};

const getMeterConfig = (elec: MeterType, gas: MeterType) => {
  if (elec && gas) {
    return elec === gas ? elec : 'mixed';
  }
  return elec || gas;
};

export const getRecommendedDirectDebitAmountToTrack = (
  friendlyAmount?: RecommendedDirectDebitResponse['friendlyAmount'],
) => {
  const amount = friendlyAmount?.amount;
  if (!amount) return `${amount}`;
  const amountAsNumber = Number(amount);
  return isNaN(amountAsNumber) ? `${amount}` : amountAsNumber;
};

interface Props {
  bootstrapData: BootstrapResponse;
  billingType: AccountBillingType;
  children: ReactNode;
}

const AnalyticsWrapper = ({
  accountId,
  customerId,
  isPayg,
  children,
}: {
  children?: ReactNode;
  accountId: string | null;
  customerId: string;
  isPayg: boolean;
}) => {
  const dispatch = useDispatch();
  const directDebitQuery = useDirectDebitQuery(accountId);

  const { elecSupply, gasSupply, isOnboarding } = useSelector(
    (state: State) => state.app,
  );

  const meterCount = elecSupply && gasSupply ? 2 : 1;
  const elecMeterType = getMeterType(elecSupply);
  const gasMeterType = getMeterType(gasSupply);
  const meterConfig = getMeterConfig(elecMeterType, gasMeterType);

  useEffect(() => {
    if (accountId != null) {
      dispatch(
        getRecommendedDirectDebitStart({ selectedAccountId: accountId }),
      );
    }
  }, [dispatch, accountId]);
  const recommendedDirectDebit = useSelector(
    (state: State) => state.recommendedDirectDebit,
  );

  const hasDirectDebit = !!directDebitQuery?.data?.amount;

  const isDebtRisk = recommendedDirectDebit.data
    ? recommendedDirectDebit.data.isDebtRisk
    : false;

  const { tariffType } = useTariffType();

  return (
    <AnalyticsData
      data={{
        account: {
          isPayg,
          isDebtRisk,
          hasDirectDebit,
          meterCount,
          isOnboarding,
          elecMeterType,
          gasMeterType,
          meterConfig,
          tariffType,
          recommendedDirectDebitAmount: getRecommendedDirectDebitAmountToTrack(
            recommendedDirectDebit?.data?.friendlyAmount,
          ),
        },
      }}
    >
      <Main
        accountId={accountId || ''}
        customerId={customerId}
        hasDirectDebit={hasDirectDebit}
        isFetched={directDebitQuery.isFetched}
        isDebtRisk={isDebtRisk}
      >
        <>
          {directDebitQuery.isLoading ? (
            <StyledLoadingBackground>
              <SiteLoading />
            </StyledLoadingBackground>
          ) : null}
          {children}
        </>
      </Main>
    </AnalyticsData>
  );
};

const WrapperWithData = ({ bootstrapData, children, billingType }: Props) => {
  const { hash, pathname } = useLocation();
  const { accountId, setAccountId } = useAccountContext();
  const hasBootstrapDataSetInRedux = useSelector(
    (state: State) => state.user.customerId,
  );
  const isPayg = billingType === 'PAYG';
  const isPayx = billingType === 'PAYX';

  const { accountIds, customerId, selectedAccountId } = bootstrapData;

  const hasMultipleAccountIds = accountIds.length > 1;

  useEffect(() => {
    spaLoaded(pathname);
    // TODO: See https://github.com/ovotech/orion-ui/issues/2861
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedAccountId != null && !accountId) {
      setAccountId(selectedAccountId);
    }
  }, [accountId, setAccountId, selectedAccountId]);

  useEffect(() => {
    if (hash === '') {
      return;
    }

    const element = document.getElementById(hash.substring(1));
    if (element) {
      element.focus();
      if (document.activeElement !== element) {
        element.tabIndex = -1;
        element.focus();
        element.removeAttribute('tabindex');
      }
    }
  });

  /*
    When this component loads, it gets bootstrap data passed into it as a prop.
    This bootstrap data needs to be set in Redux for all our legacy Redux fetching components to work (as they depend on info such as `accountId`).
    We need to ensure the app does not load BEFORE Redux has updated and re-rendered with its state that includes this info (such as `accountId`).

    The 'most reliable' indicator that Redux has updated its state is to use `customerId`, as every user who logs in has a valid `customerId`.
    Using `accountId` may be problematic as users can switch between different account IDs.
    `customerId` is set by the `dispatch(getBootstrapSuccess('', bootstrapWithSelectedAccount));` command in the parent `Wrapper`'s `useEffect`.
    So when this is available in Redux state, we know that dispatch and the Redux update has occurred, so we can render the app.

    This also ensures that `customerId` context has been set.

    This was a hidden race condition before that has surfaced with a dependency update.

    TODO: Remove the redux condition once everything has migrated from Redux to React Query
    TODO: Rather than `null`, show a nice loading screen (or continue the one in `index.ejs`)
  */
  if (!hasBootstrapDataSetInRedux || !customerId) {
    return null;
  }

  if (
    hasMultipleAccountIds &&
    !accountId &&
    selectedAccountId === null &&
    pathname !== ROUTE_SELECT_ACCOUNT
  ) {
    return (
      <Redirect
        to={{ pathname: ROUTE_SELECT_ACCOUNT, state: { prevUrl: pathname } }}
      />
    );
  }

  if (pathname === ROUTE_PAYX && !isPayx) {
    return <Redirect to={ROUTE_HOME} />;
  }

  if (
    pathname !== ROUTE_PAYX &&
    pathname !== ROUTE_SELECT_ACCOUNT &&
    pathname !== ROUTE_HELP &&
    isPayx
  ) {
    return <Redirect to={ROUTE_PAYX} />;
  }

  return (
    <NewWebRedirectGate>
      <AnalyticsWrapper
        data-testid="wrapper"
        accountId={accountId || selectedAccountId}
        customerId={customerId}
        isPayg={isPayg}
      >
        {children}
      </AnalyticsWrapper>
    </NewWebRedirectGate>
  );
};

const Wrapper = ({ children }: { children?: ReactNode }) => {
  // tmp for kapi bootstrap rollout
  // make sure Optimizely is ready so we can toggle between legacy and kapi
  // TODO CPCE-2346
  const [optimizelyReady, setOptimizelyReady] = useState(false);
  useEffect(() => {
    optimizely.onReady().then(() => {
      setOptimizelyReady(true);
    });
  }, []);
  // end tmp

  const { customerId } = useCustomerContext();

  // tmp - uncomment after `WithOptimizely` is removed CPCE-2346
  // useEffect(() => {
  //   async function fetchCustomerId() {
  //     try {
  //       const id = await getCustomerId();
  //       setCustomerId(id);
  //     } catch (err) {
  //       console.error(err);
  //       return <GlobalErrorPage id={'customer-id-error'} />;
  //     }
  //   }
  //   fetchCustomerId();
  // }, [setCustomerId]);

  const { loading, error, data } = useBootstrapQuery(customerId);
  const { pathname } = useLocation();
  const { accountId } = useAccountContext();
  const dispatch = useDispatch();
  const {
    data: billingTypeData,
    isLoading: isBillingTypeLoading,
    isError: isBillingTypeError,
  } = useAccountBillingType();

  setInitialUserErrorTrackingContext(customerId, { accountId });

  useQualtricsDataEmbed({ accountId });

  useEffect(() => {
    async function uatLogin() {
      if (process.env.NODE_ENV === 'development') {
        await devAuth();
      }
    }
    uatLogin();
    spaLoaded(pathname);
  }, [pathname]);

  // Push bootstrap data into Redux as soon as it's available, so the legacy
  // Redux consumers (including `useAccountBillingType`'s flag-off path, which
  // gates rendering below) can resolve. This must live above the billing-type
  // gate — running it inside `WrapperWithData` would deadlock, since that
  // component never mounts until the gate opens.
  useEffect(() => {
    if (!data) {
      return;
    }
    // account context takes precedence over bootstrap/cookie data
    dispatch(
      getBootstrapSuccess('', {
        ...data,
        selectedAccountId: accountId ? accountId : data.selectedAccountId,
      }),
    );
  }, [dispatch, data, accountId]);

  if (loading || !optimizelyReady || isBillingTypeLoading) {
    return <SiteLoading />;
  }

  if (error || !data || isBillingTypeError) {
    return <GlobalErrorPage id={BOOTSTRAP_ERROR} />;
  }

  return (
    <WrapperWithData bootstrapData={data} billingType={billingTypeData.type}>
      {children}
    </WrapperWithData>
  );
};

const WithKaluzaCustomerId = ({ children }: PropsWithChildren) => {
  const { setCustomerId } = useCustomerContext();
  const { data: customerId, error } = useCustomerIdQuery();
  useEffect(() => {
    if (customerId) {
      setCustomerId(customerId);
    }
  }, [setCustomerId, customerId]);
  if (error) {
    return <GlobalErrorPage id={'customer-id-error'} />;
  }
  return <Wrapper>{children}</Wrapper>;
};

export { WithKaluzaCustomerId as Wrapper };
